import { User } from "@/model/User.ts";
import {
    Intercom,
    onUnreadCountChange,
    show,
    showNewMessage,
} from "@intercom/messenger-js-sdk";
import { assertNotUndefined } from "@/util/typeguards.ts";

const support = {
    configure: (user: User) => {
        if (!import.meta.env.PROD) {
            return;
        }

        assertNotUndefined(user.intercomUserHash, "Expected intercomUserHash");
        const org = user.managementOrganization || user.currentOrganization;
        try {
            Intercom({
                app_id: "b7u8871e",
                region: "eu",
                user_id: user.id,
                name: user.name,
                email: user.email,
                created_at: new Date(user.createdAt).getTime() / 1000,
                hide_default_launcher: true,
                company: {
                    id: org.id,
                    created_at: new Date(org.createdAt).getTime(),
                    plan: {
                        name: org.plan,
                    },
                    has_payment_method: org.hasPaymentMethod,
                    trial_ends: org.trialEndsAt
                        ? new Date(org.trialEndsAt).getTime() / 1000
                        : undefined,
                },
                user_hash: user.intercomUserHash,
            });
        } catch (e) {
            console.error("Intercom failed to load", e);
        }
    },
    // Intercom's widget download competes with the initial data queries, so
    // boot it when the main thread is idle. Returns a cancel function.
    configureWhenIdle: (user: User): (() => void) => {
        if (typeof requestIdleCallback === "function") {
            const handle = requestIdleCallback(() => support.configure(user));
            return () => cancelIdleCallback(handle);
        }

        // Safari has no requestIdleCallback
        const handle = setTimeout(() => support.configure(user), 2000);
        return () => clearTimeout(handle);
    },
    openChat(message?: string) {
        if (!import.meta.env.PROD) {
            console.log("Only available in production");
            return;
        }

        if (message) {
            showNewMessage(message);
        } else {
            show();
        }
    },
    onUnreadCountChange(callback: (unreadCount: number) => void) {
        if (!import.meta.env.PROD) {
            return;
        }
        onUnreadCountChange(callback);
    },
};

export default support;
